import { SocialNetwork } from "@/api/types";

/**
 * Caricatures of the networks' marks: recognisable at a glance, drawn by us, not the trademarks.
 *
 * <p>The first version copied the real brand paths, which made four sets of brand guidelines our
 * problem. The second swung the other way to unrelated glyphs (a briefcase, a camera) and lost the
 * one thing an icon is for — being recognised without reading anything.</p>
 *
 * <p>These are the middle: each is <b>constructed from primitives</b> — a rounded rect, a circle, a
 * couple of strokes — in the silhouette of the mark it stands for, at proportions of our choosing.
 * Nothing here is traced from official artwork. They are drawn as OUTLINES, where every real mark is
 * a solid, which keeps them a coherent set of ours and visibly not the logos.</p>
 *
 * <p>No visible text label, per the brief. Each link carries an {@code aria-label} instead: a bare
 * icon link with no accessible name is unusable with a screen reader, and the label is invisible so
 * the brand is not put back on the page.</p>
 */

/** Human name of a network. Accessible labels and email copy only, never rendered as page text. */
export const NETWORK_LABELS: Record<SocialNetwork, string> = {
  LINKEDIN: "LinkedIn",
  INSTAGRAM: "Instagram",
  X: "X",
  FACEBOOK: "Facebook",
};

export function SocialIcon({
  network,
  className,
}: {
  network: SocialNetwork;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {GLYPHS[network]}
    </svg>
  );
}

const GLYPHS: Record<SocialNetwork, React.ReactNode> = {
  // A rounded tile with a dotted stem and a shoulder: the shape of a professional badge, without
  // spelling anything out.
  LINKEDIN: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="4" />
      <circle cx="7.8" cy="8" r="0.9" fill="currentColor" stroke="none" />
      <path d="M7.8 11.4v5.2" />
      <path d="M12 16.6v-5.2" />
      <path d="M12 13.6a2.4 2.4 0 0 1 4.4 1.3v1.7" />
    </>
  ),

  // Rounded tile, lens, and a corner dot — the camera silhouette, at our own proportions.
  INSTAGRAM: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="5.5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17" cy="7" r="0.9" fill="currentColor" stroke="none" />
    </>
  ),

  // Two crossing strokes. The letter itself is nobody's property; the drawn mark is.
  X: (
    <>
      <path d="M5 5l14 14" />
      <path d="M19 5L5 19" />
    </>
  ),

  // A disc with an upright and a crossbar: the rounded-f silhouette, abstracted to two strokes.
  FACEBOOK: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M14.6 7.6h-1.5a2.2 2.2 0 0 0-2.2 2.2v11" />
      <path d="M9.2 13.2h5.2" />
    </>
  ),
};
